<script setup lang="ts">
import type { IconsKeys } from '../icon/types'
import { type Snackbar, SnackbarLocation, SnackbarType } from './types'

type SettingsValues = {
  icon: IconsKeys
}

type Settings = Record<SnackbarType, SettingsValues>

type Props = {
  list: Snackbar[]
  limit?: number
}

const props = withDefaults(defineProps<Props>(), {
  limit: 3,
})

const limitedList = computed(() => props.list.slice(-props.limit))

const snackbarGroups = computed(() => {
  return {
    top: {
      type: SnackbarLocation.TOP,
      list: getSnackbarsByLocation(SnackbarLocation.TOP),
    },
    bottom: {
      type: SnackbarLocation.BOTTOM,
      list: getSnackbarsByLocation(SnackbarLocation.BOTTOM),
    },
  }
})

const settings: Settings = {
  [SnackbarType.MESSAGE]: {
    icon: 'alert-circle',
  },
  [SnackbarType.SUCCESS]: {
    icon: 'checkmark-circle',
  },
}

const getSnackbarsByLocation = (location: SnackbarLocation) => {
  return limitedList.value.filter(({ location: snackbarLocation }) => {
    return snackbarLocation === location
  })
}
</script>

<template>
  <Teleport to="body">
    <transition name="ui-fade">
      <div
        v-if="limitedList?.length"
        class="app-snackbar"
        data-test-id="snackbar"
      >
        <div
          v-for="group in snackbarGroups"
          :key="group.type"
          class="app-snackbar__group"
          data-test-id="snackbar-group"
        >
          <transition-group name="ui-list">
            <div
              v-for="snackbar in group.list"
              :key="snackbar.uuid"
              data-test-id="snackbar-item"
              :class="[
                'app-snackbar__item',
                `app-snackbar__item--type-${snackbar.type}`,
              ]"
            >
              <div class="app-snackbar__content">
                <the-icon
                  class="app-snackbar__icon"
                  color="var(--ui-white)"
                  :name="settings[snackbar.type].icon"
                />

                <div class="app-snackbar__message">{{ snackbar.message }}</div>
              </div>
            </div>
          </transition-group>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style lang="scss" scoped>
@use '../../assets/ui-layer/styles/mixins' as *;

.app-snackbar {
  position: fixed;
  top: 60px;
  right: 0;
  bottom: 8px;
  left: 0;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: space-between;
  pointer-events: none;
  user-select: none;

  @include media('>=tablet') {
    top: 157px;
  }

  &__group {
    display: flex;
    gap: 16px;
    width: 100%;

    &:first-child {
      flex-direction: column;
    }

    &:last-child {
      flex-direction: column-reverse;
    }
  }

  &__item {
    right: 0;
    left: 0;
    width: 100%;
    min-width: 335px;
    max-width: 335px;
    min-height: 40px;
    margin: 0 auto;
    border-radius: 10px;

    @include media('>=desktop-mini') {
      max-width: 350px;
    }

    &--type-message {
      background-color: var(--ui-secondary);
    }

    &--type-success {
      background-color: var(--ui-green);
    }
  }

  &__content {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px 16px;
    margin-right: auto;
    font-weight: 400;
    text-align: initial;
  }

  &__icon {
    flex-shrink: 0;
    align-self: flex-start;
    width: 24px;
    height: 24px;
  }

  &__message {
    font-size: 14px;
    font-weight: 600;
    -webkit-hyphens: auto;
    hyphens: auto;
    line-height: 19px;
    color: var(--ui-white);
  }
}
</style>
